import { useEffect, useRef, useState } from 'react'
import {
  Card,
  Chip,
  EmptyState,
  LoadingRows,
  inputClass,
  primaryBtn,
  relativeTime,
  secondaryBtn,
} from '../family-finance/shared'
import type { EventCandidate, IngestChannel, ParsedEventDraft } from '../../types/family-planner'
import { EVENT_CATEGORIES } from '../../types/family-planner'
import { acceptCandidate, ingestText, rejectCandidate } from '../../lib/family-planner-api'
import { fmtDay, fmtTime, parseLocal } from '../../lib/family-planner-analytics'
import { clearSharedText, readSharedText, stripWhatsAppTranscript } from '../../lib/planner-share'
import { CATEGORY_META, type PlannerState } from './usePlanner'

type Edits = Partial<ParsedEventDraft> & { targetCalendarId?: string }

export default function InboxTab({ state }: { state: PlannerState }) {
  const { loading, pendingCandidates, calendars, members, refresh } = state
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [edits, setEdits] = useState<Record<string, Edits>>({})
  const [paste, setPaste] = useState('')
  const [pasteChannel, setPasteChannel] = useState<IngestChannel>('whatsapp')
  const [pasteCalendar, setPasteCalendar] = useState('')

  const writableCalendars = calendars.filter((c) => !c.readOnly)
  const defaultCalendar = pasteCalendar || writableCalendars[0]?.id || ''

  async function guard(key: string, fn: () => Promise<void>) {
    setBusy(key)
    setError(null)
    setNotice(null)
    try {
      await fn()
      refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong')
    } finally {
      setBusy(null)
    }
  }

  async function handleAccept(candidate: EventCandidate) {
    await guard(`accept-${candidate.id}`, async () => {
      await acceptCandidate(candidate.id, edits[candidate.id])
      setNotice(`Added "${edits[candidate.id]?.title ?? candidate.parsed.title}" to the calendar.`)
      setEdits((prev) => {
        const next = { ...prev }
        delete next[candidate.id]
        return next
      })
    })
  }

  async function handleParse(override?: { text: string; from?: string; context?: string }) {
    const source = override?.text ?? paste
    if (!source.trim()) return
    await guard('parse', async () => {
      const candidate = await ingestText({
        text: source,
        channel: pasteChannel,
        from: override?.from,
        context: override?.context,
        targetCalendarId: defaultCalendar,
        memberIds: [],
      })
      if (!override) setPaste('')
      setNotice(
        `Parsed "${candidate.parsed.title}" at ${Math.round(candidate.confidence * 100)}% confidence — review it below.`,
      )
    })
  }

  // A message shared in from WhatsApp (Android share target, iOS Shortcut, or a
  // share link) arrives as a URL parameter. Parse it once, then drop it from the
  // URL so a reload can't duplicate it.
  const sharedHandled = useRef(false)
  useEffect(() => {
    if (sharedHandled.current || !defaultCalendar) return
    const shared = readSharedText()
    if (!shared) return
    sharedHandled.current = true
    clearSharedText()
    const { text, from } = stripWhatsAppTranscript(shared.text)
    setPaste(text)
    handleParse({
      text,
      from: from ? `${from} (shared)` : 'Shared to the planner',
      context: shared.title || 'Shared message',
    }).catch(() => {
      // The parse failure is already surfaced by guard(); the text stays in the
      // box so it can be corrected by hand.
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCalendar])

  if (loading) return <LoadingRows rows={4} />

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}
      {notice && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-3 text-emerald-600 dark:text-emerald-400 text-sm">
          {notice}
        </div>
      )}

      <Card
        title="Paste an invitation"
        subtitle="Anything with a date in it — a forwarded WhatsApp message, an email body, a text from a coach."
      >
        <textarea
          className={`${inputClass} min-h-[90px] font-mono text-xs`}
          value={paste}
          onChange={(e) => setPaste(e.target.value)}
          placeholder="Makeup game Saturday 9am at Lions Park Field 2. Arrive by 8:30."
        />
        <div className="flex flex-wrap items-end gap-3 mt-3">
          <div>
            <label className="text-slate-500 dark:text-slate-400 text-xs block mb-1">Source</label>
            <select
              className={inputClass}
              value={pasteChannel}
              onChange={(e) => setPasteChannel(e.target.value as IngestChannel)}
            >
              <option value="whatsapp">WhatsApp</option>
              <option value="gmail">Email</option>
            </select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="text-slate-500 dark:text-slate-400 text-xs block mb-1">Target calendar</label>
            <select
              className={inputClass}
              value={defaultCalendar}
              onChange={(e) => setPasteCalendar(e.target.value)}
            >
              {writableCalendars.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <button className={primaryBtn} onClick={() => handleParse()} disabled={busy === 'parse' || !paste.trim()}>
            {busy === 'parse' ? 'Parsing…' : 'Parse message'}
          </button>
        </div>
      </Card>

      <Card
        title={`Review inbox (${pendingCandidates.length})`}
        subtitle="Detected events wait here. Nothing reaches a calendar until you accept it."
      >
        {pendingCandidates.length === 0 ? (
          <EmptyState icon="📥" message="Inbox is clear — run a scan on the Sources tab to look for more." />
        ) : (
          <div className="space-y-4">
            {pendingCandidates.map((candidate) => {
              const edit = edits[candidate.id] ?? {}
              const draft = { ...candidate.parsed, ...edit }
              const duplicate = candidate.duplicateOfEventId
                ? state.events.find((e) => e.id === candidate.duplicateOfEventId)
                : null
              const confidenceTone =
                candidate.confidence >= 0.8 ? 'green' : candidate.confidence >= 0.55 ? 'amber' : 'red'
              const setEdit = (patch: Edits) =>
                setEdits((prev) => ({ ...prev, [candidate.id]: { ...prev[candidate.id], ...patch } }))

              return (
                <div
                  key={candidate.id}
                  className="border border-slate-200 dark:border-slate-700 rounded-xl p-4 bg-slate-50 dark:bg-slate-900/40"
                >
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <Chip tone={candidate.channel === 'gmail' ? 'blue' : 'green'}>
                      {candidate.channel === 'gmail' ? '✉️ Gmail' : '💬 WhatsApp'}
                    </Chip>
                    <span className="text-slate-900 dark:text-white text-sm font-medium">{candidate.context}</span>
                    <span className="text-slate-400 dark:text-slate-500 text-xs">
                      {candidate.from} · {relativeTime(candidate.receivedAt)}
                    </span>
                    <Chip tone={confidenceTone}>{Math.round(candidate.confidence * 100)}% confident</Chip>
                  </div>

                  <blockquote className="border-l-2 border-slate-300 dark:border-slate-600 pl-3 text-slate-600 dark:text-slate-300 text-xs italic mb-3">
                    {candidate.excerpt}
                  </blockquote>

                  {duplicate && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mb-3 text-amber-700 dark:text-amber-400 text-xs">
                      Looks like a duplicate of <strong>{duplicate.title}</strong> already on{' '}
                      {fmtDay(parseLocal(duplicate.start))}
                      {duplicate.allDay ? '' : ` at ${fmtTime(duplicate.start)}`}. Accepting will create a second copy.
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="sm:col-span-2">
                      <label className="text-slate-500 dark:text-slate-400 text-xs block mb-1">Title</label>
                      <input
                        className={inputClass}
                        value={draft.title}
                        onChange={(e) => setEdit({ title: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-slate-500 dark:text-slate-400 text-xs block mb-1">Starts</label>
                      <input
                        className={inputClass}
                        type={draft.allDay ? 'date' : 'datetime-local'}
                        value={draft.start}
                        onChange={(e) => setEdit({ start: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-slate-500 dark:text-slate-400 text-xs block mb-1">Ends</label>
                      <input
                        className={inputClass}
                        type={draft.allDay ? 'date' : 'datetime-local'}
                        value={draft.end}
                        onChange={(e) => setEdit({ end: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-slate-500 dark:text-slate-400 text-xs block mb-1">Location</label>
                      <input
                        className={inputClass}
                        value={draft.location ?? ''}
                        onChange={(e) => setEdit({ location: e.target.value })}
                        placeholder="—"
                      />
                    </div>
                    <div>
                      <label className="text-slate-500 dark:text-slate-400 text-xs block mb-1">Category</label>
                      <select
                        className={inputClass}
                        value={draft.category}
                        onChange={(e) => setEdit({ category: e.target.value as ParsedEventDraft['category'] })}
                      >
                        {EVENT_CATEGORIES.map((c) => (
                          <option key={c} value={c}>
                            {CATEGORY_META[c].icon} {CATEGORY_META[c].label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-slate-500 dark:text-slate-400 text-xs block mb-1">Calendar</label>
                      <select
                        className={inputClass}
                        value={edit.targetCalendarId ?? candidate.targetCalendarId}
                        onChange={(e) => setEdit({ targetCalendarId: e.target.value })}
                      >
                        {writableCalendars.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <span className="text-slate-500 dark:text-slate-400 text-xs">Who:</span>
                    {members.map((m) => {
                      const on = (draft.memberIds ?? []).includes(m.id)
                      return (
                        <button
                          key={m.id}
                          onClick={() =>
                            setEdit({
                              memberIds: on
                                ? (draft.memberIds ?? []).filter((id) => id !== m.id)
                                : [...(draft.memberIds ?? []), m.id],
                            })
                          }
                          className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                            on
                              ? 'text-white'
                              : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                          }`}
                          style={on ? { backgroundColor: m.color } : undefined}
                        >
                          {m.name}
                        </button>
                      )
                    })}
                  </div>

                  <div className="flex gap-2 mt-4">
                    <button
                      className={primaryBtn}
                      onClick={() => handleAccept(candidate)}
                      disabled={busy === `accept-${candidate.id}`}
                    >
                      {busy === `accept-${candidate.id}` ? 'Adding…' : 'Add to calendar'}
                    </button>
                    <button
                      className={secondaryBtn}
                      onClick={() => guard(`reject-${candidate.id}`, () => rejectCandidate(candidate.id))}
                      disabled={busy === `reject-${candidate.id}`}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Card>
    </div>
  )
}
